import { hasValue } from 'utils/hasValue';

import {
  AMOUNT_ABOVE_MAXIMUM_ERROR_CODE,
  DEFAULT_EXCHANGE_ERROR_MESSAGE,
  exchangeErrorLookup
} from './constants';

const amountAboveMaximumMessage = (maxAmount: string, asset: string) =>
  `You can exchange up to ${maxAmount} ${asset} at a time right now. To exchange more, split it into multiple exchanges.`;

const isErrorObject = (value: unknown): value is Record<string, unknown> =>
  hasValue(value) && typeof value === 'object';

const extractExchangeErrorCode = (error: unknown): string | undefined => {
  if (!isErrorObject(error)) return undefined;
  if ('error' in error) return extractExchangeErrorCode(error.error);

  return typeof error.details === 'string' ? error.details : undefined;
};

export const extractExchangeError = (error: unknown): string => {
  if (typeof error === 'string') {
    return (
      exchangeErrorLookup[error] ||
      error.trim() ||
      DEFAULT_EXCHANGE_ERROR_MESSAGE
    );
  }

  if (!isErrorObject(error)) {
    return DEFAULT_EXCHANGE_ERROR_MESSAGE;
  }

  if ('error' in error) return extractExchangeError(error.error);

  const { details } = error;
  if (typeof details === 'string') return extractExchangeError(details);
  if (!isErrorObject(details)) {
    return DEFAULT_EXCHANGE_ERROR_MESSAGE;
  }

  const messages = Object.entries(details).flatMap(([field, errors]) => {
    const label = field.replace(/_/g, ' ');
    return (Array.isArray(errors) ? errors : [errors])
      .filter(
        (message): message is string =>
          typeof message === 'string' && message.trim() !== ''
      )
      .map((message) =>
        field === 'base'
          ? extractExchangeError(message)
          : `${label.charAt(0).toUpperCase()}${label.slice(1)} ${message}`
      );
  });

  return messages.join('; ') || DEFAULT_EXCHANGE_ERROR_MESSAGE;
};

export const extractQuoteError = (
  error: unknown,
  maxAmount: string | null | undefined,
  asset: string | undefined
): string => {
  if (
    extractExchangeErrorCode(error) === AMOUNT_ABOVE_MAXIMUM_ERROR_CODE &&
    hasValue(maxAmount) &&
    hasValue(asset)
  ) {
    return amountAboveMaximumMessage(maxAmount, asset);
  }

  return extractExchangeError(error);
};
